import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import Dynamic from '@base/Dynamic/Dynamic';
import { TEST_SIGNUP_BANNER } from '@comp/SignupGenericBanner/constant';
import { TEST_SERIES_EVENTS } from '@cont/SeeAll/constants';
import {
  setTestSeriesReferralInStorage,
  isCbtProgramme,
  getCentreChangeErrorMessage
} from '@cont/SeeAll/utils';
import styled from '@emotion/styled';
import useTestSeriesStore from '@stores/test-series';
import { useSnackbar } from '@stores/snackbar';
import { mediaQuery, Spacings } from '@styles/index';
import { getParameterByName } from '@utils/textHelper';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import useCbtVenueScreen from '@cont/SeeAll/useCbtVenueScreen';
import { changeCbtCentre } from '@api/fetchers/cbt';
import Header from './Header';
import Sections from './Sections';
import useGoalSubscription from './useGoalSubscription';
import useSkuDetails from './useSkuDetails';

import useTestSeriesEvents from './useTestSeriesEvents';
import useTestSeriesInfo from './useTestSeriesInfo';
import CbtVenueSelectionScreen from './CbtVenueSelectionScreen';
import useCbtVenueCheckout from './useCbtVenueCheckout';

const SignupGenericBanner = Dynamic(() =>
  import('@comp/SignupGenericBanner/SignupGenericBanner')
);

const Wrapper = styled.div`
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const StyledSignupBanner = styled(SignupGenericBanner)`
  margin-top: ${Spacings.SPACING_10B};
`;

const TestSeriesListing = ({ fromStore }) => {
  const [isStickyHeaderShown, setIsStickyHeaderShown] = useState(false);
  const {
    showCbtVenueSelection,
    isEditMode,
    openCbtVenueSelection,
    closeCbtVenueSelection
  } = useCbtVenueScreen();
  const { proceedToCheckout, getUserDetailsSubmittedProps } =
    useCbtVenueCheckout();
  const { data, mutate: mutateTestSeriesInfo } = useTestSeriesInfo();
  const { open: showSnackbar } = useSnackbar();
  const { goalUid, isSpecial, isStoreExclusive, programmeType } = data;
  const isCbt = isCbtProgramme(programmeType);
  const shouldShowStoreDetails = fromStore || isStoreExclusive;

  const { query, asPath } = useRouter();

  const referralCode = getParameterByName('referral_code', asPath);

  useEffect(() => {
    if (referralCode && query.courseUID) {
      setTestSeriesReferralInStorage(query.courseUID, referralCode);
    }
  }, [query.courseUID, referralCode]);

  const { isSubscribed } = useGoalSubscription();
  const {
    isSkuAvailable,
    isExpired,
    isValidating,
    mutate: refectchSku,
    refetchUam,
    isError,
    hasSkuAccess
  } = useSkuDetails(goalUid, query.courseUID);

  const { sendViewedTestSeriesEvent } = useTestSeriesEvents(hasSkuAccess);

  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { isValidating: isMyInfoValidating } = useMyInfo();

  const isStoreDetailsShown = () => {
    if (shouldShowStoreDetails) {
      if (isError) {
        return false;
      }
      if (isValidating) {
        return true;
      }
      if (!isLoggedIn && isSkuAvailable) {
        return true;
      }
      if (hasSkuAccess) {
        return true; // show sku purchased flow
      }

      if (isExpired) return false;
      if (isCbt) return true;
      // hasSkuAccess is already false here (checked above), so a store-
      // exclusive test series still needs a purchase despite the goal
      // subscription — only non-exclusive content can skip the store flow.
      if (isSubscribed && !isStoreExclusive) {
        return false;
      }

      if (isSkuAvailable) {
        return true; // show sku purchase flow
      }
    }
    if (isMyInfoValidating || isValidating) {
      return true;
    }
    if (hasSkuAccess) {
      return true; // show sku purchased flow
    }

    return false;
  };

  const { isSkuInfoFetchedOnClient, isUamInfoFetchedOnClient } =
    useTestSeriesStore();
  const viewEventFiredRef = useRef(false);

  useEffect(() => {
    if (
      (((!isLoggedIn || isSubscribed || isSpecial) &&
        isSkuInfoFetchedOnClient) ||
        isUamInfoFetchedOnClient) &&
      !viewEventFiredRef.current
    ) {
      viewEventFiredRef.current = true;
      sendViewedTestSeriesEvent(TEST_SERIES_EVENTS.TEST_SERIES_VIEWED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSkuInfoFetchedOnClient, isUamInfoFetchedOnClient]);

  useEffect(() => {
    if (isLoggedIn) {
      refectchSku();
      refetchUam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const { remainingCentreChanges, canChangeCentre } =
    data.enrolmentDetails ?? {};

  const handleCentreChange = useCallback(
    async (venueSelection) => {
      const centreUid = venueSelection?.centreUid;
      if (!query.courseUID || !centreUid) return;

      const response = await changeCbtCentre(query.courseUID, centreUid);
      if (response?.error) {
        showSnackbar({
          message: getCentreChangeErrorMessage(response.error),
          type: 'error'
        });
        return;
      }

      await mutateTestSeriesInfo();
      closeCbtVenueSelection();
    },
    [
      query.courseUID,
      showSnackbar,
      mutateTestSeriesInfo,
      closeCbtVenueSelection
    ]
  );

  if (isCbt && showCbtVenueSelection) {
    return (
      <CbtVenueSelectionScreen
        onBack={closeCbtVenueSelection}
        onProceed={isEditMode ? handleCentreChange : proceedToCheckout}
        getSegmentProps={getUserDetailsSubmittedProps}
        isEditMode={isEditMode}
        remainingCentreChanges={remainingCentreChanges}
        canChangeCentre={canChangeCentre}
        prefill={data.enrolmentDetails}
      />
    );
  }

  return (
    <Wrapper>
      <Header
        fromStore={isStoreDetailsShown()}
        setIsStickyHeaderShown={setIsStickyHeaderShown}
        onOpenCbtVenueSelection={openCbtVenueSelection}
      />
      <Sections
        fromStore={isStoreDetailsShown()}
        isStickyHeaderShown={isStickyHeaderShown}
        onOpenCbtVenueSelection={openCbtVenueSelection}
      />
      <StyledSignupBanner data={TEST_SIGNUP_BANNER} goalUID={goalUid} />
    </Wrapper>
  );
};

export default TestSeriesListing;
